import React from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { openCookieSettings } from './ConsentBanner';

export const CONTACT_EMAIL = 'hello@overall-wellbeing.com';

const LINK_COLUMNS = [
  {
    title: 'Explore',
    links: [
      { label: 'Home', to: '/' },
      { label: 'Longevity', to: '/longevity' },
      { label: 'Wellbeing', to: '/wellbeing' },
      { label: 'Resources', to: '/resources' },
    ],
  },
  {
    title: 'Your Journey',
    links: [
      { label: 'Start with the Essentials', to: '/longevity' },
      { label: 'Explore Areas of Life', to: '/wellbeing' },
      { label: 'Dive into the Library', to: '/resources' },
      { label: 'Build Everyday Habits', to: '/my-login' },
    ],
  },
  {
    title: 'Your Space',
    links: [
      { label: 'My Login', to: '/my-login' },
      { label: 'About', to: '/about' },
      { label: 'Editorial Standards', to: '/editorial-standards' },
      { label: 'Contact', to: '/contact' },
    ],
  },
];

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="w-full bg-surface-container-lowest border-t border-surface-container" data-testid="site-footer">
      <div className="w-full px-margin-mobile lg:px-margin pt-space-3xl pb-space-2xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-gutter mb-space-3xl">
          <div className="flex flex-col">
            <Link to="/" className="inline-flex max-w-[220px]" aria-label="OVERALL WELLBEING — Live Better. Live Longer. — Home">
              <img src="/logo-lockup.png" alt="OVERALL WELLBEING — Live Better. Live Longer." className="w-full h-auto" />
            </Link>
            <p className="font-body-md text-body-md text-on-surface-variant mt-space-md leading-relaxed max-w-xs">
              Clear, friendly guidance to help you understand your wellbeing and build small habits that last.
            </p>
          </div>

          {LINK_COLUMNS.map((col) => (
            <div key={col.title} className="flex flex-col">
              <h3 className="font-headline-sm text-headline-sm tracking-tight text-outline mb-space-lg uppercase">{col.title}</h3>
              <ul className="flex flex-col gap-space-sm">
                {col.links.map((item) => {
                  const testId = `footer-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
                  const cls = 'font-body-md text-body-md text-on-surface-variant hover:text-primary transition-colors';
                  return (
                    <li key={item.label}>
                      {item.href ? (
                        <a href={item.href} data-testid={testId} className={cls}>{item.label}</a>
                      ) : (
                        <Link to={item.to} data-testid={testId} className={cls}>{item.label}</Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="w-full p-space-lg rounded-lg bg-primary-container border-l-4 border-surface-tint mb-space-2xl">
          <div className="flex items-start gap-space-md">
            <Icon name="verified" className="text-surface-tint mt-1 text-[24px]" />
            <div>
              <p className="font-title-md text-title-md text-outline uppercase tracking-wider mb-space-xs">Guidance, Not Medical Advice</p>
              <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">
                OVERALL WELLBEING is an educational and lifestyle guidance platform. We share knowledge based on trusted
                health sources to help you understand your body and mind. We do not diagnose or treat medical conditions.
                Please speak with a qualified health professional about any medical concern.
              </p>
            </div>
          </div>
        </div>

        <div className="pt-space-lg border-t border-surface-container flex flex-col md:flex-row items-center justify-between gap-space-md">
          <div className="flex flex-col md:flex-row md:items-center gap-space-xs md:gap-space-md text-center md:text-left">
            <p className="font-body-sm text-body-sm text-on-surface-variant" data-testid="footer-copyright">
              © {year} OVERALL WELLBEING. All rights reserved.
            </p>
            <span className="font-label-sm text-label-sm uppercase tracking-widest text-surface-tint font-bold">Live Better. Live Longer.</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-space-xs sm:gap-space-lg">
            <p className="font-body-sm text-body-sm text-on-surface-variant" data-testid="footer-contact">
              Questions?{' '}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="footer-contact-email"
                className="text-surface-tint font-semibold hover:text-primary transition-colors break-all"
              >
                {CONTACT_EMAIL}
              </a>
            </p>
            <span className="flex flex-wrap items-center gap-space-lg">
              <Link to="/about" className="font-body-sm text-body-sm text-on-surface-variant hover:text-primary transition-colors" data-testid="footer-about-link">About</Link>
              <Link to="/editorial-standards" className="font-body-sm text-body-sm text-on-surface-variant hover:text-primary transition-colors" data-testid="footer-editorial-link">Editorial Standards</Link>
              <Link to="/medical-disclaimer" className="font-body-sm text-body-sm text-on-surface-variant hover:text-primary transition-colors" data-testid="footer-disclaimer-link">Medical Disclaimer</Link>
              <Link to="/privacy" className="font-body-sm text-body-sm text-on-surface-variant hover:text-primary transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
              <Link to="/terms" className="font-body-sm text-body-sm text-on-surface-variant hover:text-primary transition-colors" data-testid="footer-terms-link">Terms of Use</Link>
              <button
                type="button"
                onClick={openCookieSettings}
                data-testid="footer-cookie-settings-link"
                className="font-body-sm text-body-sm text-on-surface-variant hover:text-primary transition-colors"
              >
                Cookie settings
              </button>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
